import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import TechnicalSignalIcons from '../TechnicalSignalIcons';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { useWatchlistLabels, assignLabel, unassignLabel } from '../../services/api';
import { useLogin } from '../../contexts/LoginContext';
import CallHighlights from './CallHighlights';

const fmt = (price) => price == null ? '--' : `$${Number(price).toFixed(2)}`;
const fmtPct = (pct) => {
  if (pct == null) return null;
  const v = Number(pct);
  return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
};
const fmtCap = (n) => {
  if (n == null) return null;
  if (n >= 1e12) return `$${(n / 1e12).toFixed(1)}T`;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(0)}M`;
  return n.toLocaleString();
};

const sentimentBorder = (score) => {
  if (score == null) return '#e5e7eb';
  if (score >= 0.7) return '#34d399';
  if (score >= 0.5) return '#60a5fa';
  if (score >= 0.3) return '#fbbf24';
  return '#f87171';
};

// Call/put volume ratio → background tint. Tiered so that mild skews are barely visible
// but strong skews (3x+ either way) clearly stand out. Empty/null → white.
const ratioBackground = (ratio) => {
  if (ratio == null) return '#fff';
  if (ratio >= 5) return '#bbf7d0';    // extreme bullish (>=5x calls)
  if (ratio >= 3) return '#dcfce7';    // strong bullish
  if (ratio >= 2) return '#ecfdf5';    // bullish
  if (ratio >= 1.5) return '#f0fdf4';  // mild bullish
  if (ratio > 0.67) return '#fff';     // neutral band (0.67x–1.5x)
  if (ratio > 0.5) return '#fef2f2';   // mild bearish
  if (ratio > 0.33) return '#fee2e2';  // bearish
  if (ratio > 0.2) return '#fecaca';   // strong bearish
  return '#fca5a5';                     // extreme bearish (>=5x puts)
};

// Returns { display, side } where side is 'call'|'put',
// display is "3.1× C" / "2.4× P", or null when too neutral to surface (0.67..1.5 band).
const interpretRatio = (ratio) => {
  if (ratio == null) return null;
  if (ratio >= 1.5) return { display: `${ratio.toFixed(1)}× C`, side: 'call' };
  if (ratio < 0.67) {
    const inv = 1 / ratio;
    return { display: `${inv.toFixed(1)}× P`, side: 'put' };
  }
  return null;
};

const fmtVol = (n) => {
  if (n == null) return '--';
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toLocaleString();
};

const fmtIV = (iv) => iv == null ? '--' : `${(iv * 100).toFixed(0)}%`;

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
};

// Base style for the fixed-width 18×18 icon badges in the compact row (signal/volume/news).
const iconBadge = (extra) => ({
  width: 18, height: 18, borderRadius: 4, flexShrink: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  lineHeight: 1, ...extra,
});

const MEANINGFUL_SESSION_PCT = 0.5;
// Returns {label, pct, isPositive} for whichever of post/pre-market has meaningful movement, else null.
// Post-market wins if both are present (it's the more recent session).
const getSessionBadge = (s) => {
  const ahPct = s.post_market_change_percent;
  if (ahPct != null && Math.abs(ahPct) > MEANINGFUL_SESSION_PCT) {
    return { label: 'AH', pct: ahPct, isPositive: ahPct >= 0 };
  }
  const pmPct = s.pre_market_change_percent;
  if (pmPct != null && Math.abs(pmPct) > MEANINGFUL_SESSION_PCT) {
    return { label: 'PM', pct: pmPct, isPositive: pmPct >= 0 };
  }
  return null;
};

// Inline label pills — all labels shown as small togglable pills; assigned = filled, unassigned = outlined.
// "+ New" pill opens the label manager modal to create a label.
const InlineLabelPills = ({ symbol, userEmail, onOpenLabelManager }) => {
  const queryClient = useQueryClient();
  const { data: labelsData = { labels: [], assignments: {} } } = useWatchlistLabels(userEmail);
  const assignedIds = new Set(labelsData.assignments?.[symbol]?.labels || []);
  const [pendingId, setPendingId] = useState(null);

  const toggleMutation = useMutation({
    mutationFn: ({ labelId, assign }) =>
      assign ? assignLabel({ symbol, labelId }) : unassignLabel({ symbol, labelId }),
    onMutate: async ({ labelId, assign }) => {
      setPendingId(labelId);
      await queryClient.cancelQueries(['watchlistLabels', userEmail]);
      const previous = queryClient.getQueryData(['watchlistLabels', userEmail]);
      queryClient.setQueryData(['watchlistLabels', userEmail], (old) => {
        if (!old) return old;
        const newData = { ...old, assignments: { ...old.assignments } };
        const current = newData.assignments[symbol] || { labels: [], purchases: {} };
        const labels = assign
          ? Array.from(new Set([...current.labels, labelId]))
          : current.labels.filter((id) => id !== labelId);
        newData.assignments[symbol] = { ...current, labels };
        return newData;
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['watchlistLabels', userEmail], context.previous);
      }
    },
    onSettled: () => {
      setPendingId(null);
      queryClient.invalidateQueries(['watchlistLabels', userEmail]);
    },
  });

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
      {labelsData.labels.map((label) => {
        const isAssigned = assignedIds.has(label.id);
        const isPending = pendingId === label.id;
        return (
          <button
            key={label.id}
            type="button"
            disabled={isPending}
            onClick={(e) => { e.stopPropagation(); toggleMutation.mutate({ labelId: label.id, assign: !isAssigned }); }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 10, padding: '2px 8px', borderRadius: 999,
              border: `1px solid ${isAssigned ? label.color : '#e5e7eb'}`,
              background: isAssigned ? label.color : '#fff',
              color: isAssigned ? '#fff' : '#4b5563',
              fontWeight: 600, cursor: isPending ? 'wait' : 'pointer', lineHeight: 1.4,
              opacity: isPending ? 0.55 : 1,
              transition: 'background 0.1s, color 0.1s',
            }}
          >
            <span style={{
              width: 6, height: 6, borderRadius: '50%',
              background: isAssigned ? '#fff' : label.color,
              display: 'inline-block',
            }} />
            {label.name}
          </button>
        );
      })}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onOpenLabelManager?.(); }}
        style={{
          display: 'inline-flex', alignItems: 'center',
          fontSize: 10, padding: '2px 8px', borderRadius: 999,
          background: '#fff', border: '1px dashed #d1d5db',
          color: '#6b7280', fontWeight: 600, cursor: 'pointer', lineHeight: 1.4,
        }}
      >
        + New
      </button>
    </div>
  );
};


const EarningsTile = ({ stock, loggedInUser, isExpanded, onToggle, onLoginSuccess, onOpenLabelManager, news, callHighlights }) => {
  const { triggerLogin } = useLogin();
  const s = stock;
  const daily = s.regular_market_change_percent;
  const pos = (daily || 0) >= 0;
  const weekPos = s.fifty_two_week_position != null ? (s.fifty_two_week_position * 100) : null;
  const newsArticles = news || [];
  const hasNews = newsArticles.length > 0;
  const sessionBadge = getSessionBadge(s);
  const opt = s.options || null;
  const ratioInfo = opt ? interpretRatio(opt.call_put_ratio) : null;
  const tileBg = ratioBackground(opt?.call_put_ratio);

  return (
    <div
      style={{ borderLeft: `3px solid ${sentimentBorder(s.sentiment_score)}`, borderRadius: 8, overflow: 'hidden', cursor: 'pointer', background: tileBg }}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
    >
      {/* ── Compact row ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px' }}>
        {/* Logo */}
        {s.logo_url ? (
          <img src={s.logo_url} alt="" style={{ width: 24, height: 24, objectFit: 'contain', borderRadius: 4, flexShrink: 0 }} onError={(e) => { e.target.style.display = 'none'; }} />
        ) : (
          <div style={{ width: 24, height: 24, borderRadius: 4, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af' }}>{s.symbol?.slice(0, 2)}</span>
          </div>
        )}

        {/* Symbol + Name */}
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontWeight: 700, fontSize: 12, color: '#111827', whiteSpace: 'nowrap' }}>{s.symbol}</span>
            <span style={{ fontSize: 11, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.companyName}</span>
          </div>
        </div>

        {/* Compact signal + volume + news indicators */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {/* Signal: colored arrow */}
          {s.latest_signal && (
            <span
              title={`${s.latest_signal.type} ${s.latest_signal.name || ''}`}
              style={iconBadge({
                fontSize: 11, fontWeight: 800,
                background: s.latest_signal.type === 'BUY' ? '#ecfdf5' : '#fef2f2',
                color: s.latest_signal.type === 'BUY' ? '#047857' : '#dc2626',
              })}
            >
              {s.latest_signal.type === 'BUY' ? '▲' : '▼'}
            </span>
          )}
          {/* Volume: colored bar icon */}
          {s.volume_trend != null && Math.abs(s.volume_trend - 1) > 0.05 && (
            <span
              title={`10d avg vol ${s.volume_trend > 1 ? '+' : ''}${((s.volume_trend - 1) * 100).toFixed(0)}% vs 3mo avg`}
              style={iconBadge({
                fontSize: 10, fontWeight: 700,
                background: s.volume_trend > 1.1 ? '#ecfdf5' : s.volume_trend < 0.9 ? '#fef2f2' : '#f9fafb',
                color: s.volume_trend > 1.1 ? '#059669' : s.volume_trend < 0.9 ? '#ef4444' : '#9ca3af',
              })}
            >
              V
            </span>
          )}
          {/* News indicator */}
          {hasNews && (
            <span
              title={`${newsArticles.length} recent article${newsArticles.length !== 1 ? 's' : ''}`}
              style={iconBadge({ fontSize: 10, fontWeight: 700, background: '#eef2ff', color: '#4f46e5' })}
            >
              N
            </span>
          )}
          {/* Options C/P ratio chip — variable-width, only shown when meaningfully skewed */}
          {ratioInfo && (
            <span
              title={`Front-month options: ${fmtVol(opt.call_volume)} calls vs ${fmtVol(opt.put_volume)} puts (exp ${opt.expiration_date})`}
              style={{
                height: 18, borderRadius: 4, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0 6px',
                fontSize: 10, fontWeight: 800, lineHeight: 1,
                background: ratioInfo.side === 'call' ? '#d1fae5' : '#fecaca',
                color: ratioInfo.side === 'call' ? '#065f46' : '#991b1b',
              }}
            >
              {ratioInfo.display}
            </span>
          )}
        </div>

        {/* Price + daily change + AH/PM */}
        <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 62 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', fontFamily: 'ui-monospace, monospace' }}>{fmt(s.current_price)}</div>
          {daily != null && (
            <div style={{ fontSize: 10, fontWeight: 600, fontFamily: 'ui-monospace, monospace', color: pos ? '#059669' : '#ef4444' }}>
              {fmtPct(daily)}
            </div>
          )}
          {sessionBadge && (
            <div style={{ fontSize: 9, fontWeight: 700, fontFamily: 'ui-monospace, monospace', color: sessionBadge.isPositive ? '#059669' : '#ef4444', marginTop: 1 }}>
              <span style={{ color: '#9ca3af', fontWeight: 500 }}>{sessionBadge.label} </span>
              {fmtPct(sessionBadge.pct)}
            </div>
          )}
        </div>

        {/* Chevron */}
        <KeyboardArrowDownIcon
          sx={{ fontSize: 16, color: '#9ca3af', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)', flexShrink: 0 }}
        />
      </div>

      {/* ── Expanded detail ── */}
      {isExpanded && (
        <div onClick={(e) => e.stopPropagation()} style={{ borderTop: '1px solid #f3f4f6', padding: '10px 12px 12px', background: '#fafbfc' }}>

          {/* Company description */}
          {s.description && (
            <div style={{
              fontSize: 11, color: '#6b7280', lineHeight: 1.4, marginBottom: 10,
              overflow: 'hidden', textOverflow: 'ellipsis',
              display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical',
            }}>
              {s.description}
            </div>
          )}

          {/* Key stats row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            {sessionBadge && (
              <span style={{
                fontSize: 11, padding: '3px 8px', borderRadius: 6, fontWeight: 600,
                background: sessionBadge.isPositive ? '#ecfdf5' : '#fef2f2',
                color: sessionBadge.isPositive ? '#047857' : '#dc2626',
              }}>
                <span style={{ color: '#9ca3af', fontWeight: 500 }}>{sessionBadge.label} </span>
                {fmtPct(sessionBadge.pct)}
              </span>
            )}
            {s.market_cap != null && (
              <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, background: '#f3f4f6' }}>
                <span style={{ color: '#9ca3af' }}>Cap </span>
                <span style={{ fontWeight: 600, color: '#374151' }}>{fmtCap(s.market_cap)}</span>
              </span>
            )}
            {s.pe_ratio != null && (
              <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, background: '#f3f4f6' }}>
                <span style={{ color: '#9ca3af' }}>P/E </span>
                <span style={{ fontWeight: 600, color: '#374151' }}>{Number(s.pe_ratio).toFixed(1)}</span>
              </span>
            )}
            {s.ma_alignment && (
              <span style={{
                fontSize: 11, padding: '3px 8px', borderRadius: 6,
                background: s.ma_alignment === 'bullish' ? '#ecfdf5' : '#fef2f2',
                color: s.ma_alignment === 'bullish' ? '#047857' : '#dc2626',
                fontWeight: 600
              }}>
                MA {s.ma_alignment === 'bullish' ? 'Bullish' : 'Bearish'}
              </span>
            )}
            {s.latest_signal && (
              <span style={{
                fontSize: 11, padding: '3px 8px', borderRadius: 6,
                background: s.latest_signal.type === 'BUY' ? '#ecfdf5' : '#fef2f2',
                color: s.latest_signal.type === 'BUY' ? '#047857' : '#dc2626',
                fontWeight: 600,
              }}>
                {s.latest_signal.type}{s.latest_signal.name ? ` ${s.latest_signal.name}` : ''}
              </span>
            )}
            {s.volume_trend != null && (
              <span style={{
                fontSize: 11, padding: '3px 8px', borderRadius: 6,
                background: s.volume_trend > 1.1 ? '#ecfdf5' : s.volume_trend < 0.9 ? '#fef2f2' : '#f3f4f6',
                color: s.volume_trend > 1.1 ? '#059669' : s.volume_trend < 0.9 ? '#ef4444' : '#6b7280',
                fontWeight: 600,
              }}>
                <span style={{ color: '#9ca3af', fontWeight: 400 }}>Vol </span>
                {s.volume_trend > 1 ? '+' : ''}{((s.volume_trend - 1) * 100).toFixed(0)}%
              </span>
            )}
            {s.industry && (
              <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, background: '#f3f4f6', color: '#6b7280' }}>
                {s.industry}
              </span>
            )}
          </div>

          {/* Technical signals */}
          {s.signals?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <TechnicalSignalIcons signals={s.signals} />
            </div>
          )}

          {/* Price momentum */}
          {(() => {
            const changes = [['3d', s.price_change_3d], ['7d', s.price_change_7d], ['14d', s.price_change_14d], ['1m', s.price_change_1m], ['3m', s.price_change_3m]].filter(([, v]) => v != null);
            if (!changes.length) return null;
            return (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Price Change</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {changes.map(([label, val]) => {
                    const v = Number(val);
                    const isPos = v >= 0;
                    return (
                      <span key={label} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                        background: isPos ? '#ecfdf5' : '#fef2f2',
                        color: isPos ? '#047857' : '#dc2626'
                      }}>
                        <span style={{ color: '#9ca3af', fontWeight: 400 }}>{label}</span>
                        {fmtPct(v)}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Options Flow */}
          {opt && opt.call_put_ratio != null && (() => {
            const totalVol = opt.call_volume + opt.put_volume;
            const callPct = (opt.call_volume / totalVol) * 100;
            const putPct = 100 - callPct;
            const ivSkew = (opt.call_iv_avg != null && opt.put_iv_avg != null)
              ? opt.call_iv_avg - opt.put_iv_avg
              : null;
            const oiRatio = opt.put_oi > 0 ? opt.call_oi / opt.put_oi : null;
            return (
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 9, color: '#9ca3af', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Options Flow
                  </span>
                  <span style={{ fontSize: 9, color: '#9ca3af' }}>
                    exp {opt.expiration_date}
                  </span>
                </div>
                {/* Hero ratio */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 6 }}>
                  <span style={{
                    fontSize: 18, fontWeight: 800, fontFamily: 'ui-monospace, monospace',
                    color: ratioInfo?.side === 'put' ? '#dc2626' : ratioInfo?.side === 'call' ? '#059669' : '#6b7280',
                  }}>
                    {ratioInfo ? ratioInfo.display : '~1×'}
                  </span>
                  <span style={{ fontSize: 11, color: '#6b7280' }}>
                    {ratioInfo?.side === 'call' ? 'more calls than puts'
                      : ratioInfo?.side === 'put' ? 'more puts than calls'
                      : 'balanced'}
                  </span>
                </div>
                {/* Volume bar: calls (green) vs puts (red) */}
                <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: '#f3f4f6', marginBottom: 4 }}>
                  <div style={{ width: `${callPct}%`, background: '#10b981' }} title={`Calls: ${fmtVol(opt.call_volume)} (${callPct.toFixed(0)}%)`} />
                  <div style={{ width: `${putPct}%`, background: '#ef4444' }} title={`Puts: ${fmtVol(opt.put_volume)} (${putPct.toFixed(0)}%)`} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#6b7280', marginBottom: 8 }}>
                  <span><span style={{ color: '#059669', fontWeight: 600 }}>{fmtVol(opt.call_volume)}</span> calls</span>
                  <span><span style={{ color: '#dc2626', fontWeight: 600 }}>{fmtVol(opt.put_volume)}</span> puts</span>
                </div>
                {/* Sub-stats */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {oiRatio != null && (
                    <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#f3f4f6' }}>
                      <span style={{ color: '#9ca3af' }}>OI </span>
                      <span style={{ fontWeight: 600, color: '#374151' }}>
                        {oiRatio >= 1 ? `${oiRatio.toFixed(1)}× C` : `${(1 / oiRatio).toFixed(1)}× P`}
                      </span>
                    </span>
                  )}
                  {opt.call_iv_avg != null && (
                    <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#f3f4f6' }}>
                      <span style={{ color: '#9ca3af' }}>IV C </span>
                      <span style={{ fontWeight: 600, color: '#374151' }}>{fmtIV(opt.call_iv_avg)}</span>
                    </span>
                  )}
                  {opt.put_iv_avg != null && (
                    <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#f3f4f6' }}>
                      <span style={{ color: '#9ca3af' }}>IV P </span>
                      <span style={{ fontWeight: 600, color: '#374151' }}>{fmtIV(opt.put_iv_avg)}</span>
                    </span>
                  )}
                  {ivSkew != null && Math.abs(ivSkew) > 0.02 && (
                    <span style={{
                      fontSize: 10, padding: '2px 6px', borderRadius: 4,
                      background: ivSkew > 0 ? '#ecfdf5' : '#fef2f2',
                      color: ivSkew > 0 ? '#047857' : '#dc2626',
                      fontWeight: 600,
                    }} title="Calls minus puts IV. Positive = upside skew, negative = downside fear">
                      Skew {ivSkew > 0 ? '+' : ''}{(ivSkew * 100).toFixed(0)}pp
                    </span>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Historical earnings */}
          {s.historical_earnings?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Past Earnings</div>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(s.historical_earnings.length, 4)}, 1fr)`, gap: 4 }}>
                {s.historical_earnings.slice(0, 4).map((q, i) => {
                  const v = Number(q.change_pct);
                  const p = v >= 0;
                  return (
                    <div key={i} style={{
                      borderRadius: 6, padding: '6px 4px', textAlign: 'center',
                      background: p ? '#ecfdf5' : '#fef2f2'
                    }}>
                      <div style={{ fontSize: 12, fontWeight: 700, fontFamily: 'ui-monospace, monospace', color: p ? '#047857' : '#dc2626' }}>
                        {fmtPct(v)}
                      </div>
                      <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 2 }}>
                        {q.date ? new Date(q.date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) : `Q${i + 1}`}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Glassdoor */}
          {(s.glassdoor_outlook != null || s.glassdoor_ai_sentiment) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 10, color: '#9ca3af' }}>Glassdoor</span>
              {s.glassdoor_outlook != null && <span style={{ fontSize: 11, color: '#374151', fontWeight: 600 }}>{Number(s.glassdoor_outlook).toFixed(2)}</span>}
              {s.glassdoor_ai_sentiment && (
                <span style={{
                  padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                  background: s.glassdoor_ai_sentiment === 'positive' ? '#ecfdf5' : s.glassdoor_ai_sentiment === 'negative' ? '#fef2f2' : '#f3f4f6',
                  color: s.glassdoor_ai_sentiment === 'positive' ? '#047857' : s.glassdoor_ai_sentiment === 'negative' ? '#dc2626' : '#6b7280'
                }}>{s.glassdoor_ai_sentiment}</span>
              )}
            </div>
          )}

          {/* 52W bar (mobile) */}
          {weekPos != null && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>52-Week Range</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 10, color: '#9ca3af', fontFamily: 'ui-monospace, monospace' }}>{fmt(s.fifty_two_week_low)}</span>
                <div style={{ position: 'relative', flex: 1, height: 6, borderRadius: 3, background: '#e5e7eb' }}>
                  <div style={{ position: 'absolute', inset: '0 auto 0 0', borderRadius: 3, width: `${weekPos}%`, background: 'linear-gradient(to right, #fca5a5, #fde68a, #6ee7b7)' }} />
                  <div style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', left: `calc(${weekPos}% - 5px)`, width: 10, height: 10, borderRadius: '50%', background: '#1f2937', border: '2px solid #fff' }} />
                </div>
                <span style={{ fontSize: 10, color: '#9ca3af', fontFamily: 'ui-monospace, monospace' }}>{fmt(s.fifty_two_week_high)}</span>
                <span style={{ fontSize: 11, color: '#374151', fontWeight: 700, fontFamily: 'ui-monospace, monospace' }}>{weekPos.toFixed(0)}%</span>
              </div>
            </div>
          )}

          {/* Earnings call highlights — rendered above news: the call is the primary source,
              the news items are secondary coverage of it. */}
          {callHighlights && (
            <div style={{ marginBottom: 10 }}>
              {/* The reaction comes off the newest historical_earnings entry, which the
                  earnings-calendar worker derives from the same shared helper the expanded row's
                  field does. The panel drops the pill itself when that entry belongs to an older
                  quarter than the call, so no date check is needed here. */}
              <CallHighlights
                highlights={callHighlights}
                reaction={{
                  percent: s.historical_earnings?.[0]?.change_pct,
                  date: s.historical_earnings?.[0]?.date,
                }}
                compact
              />
            </div>
          )}

          {/* News */}
          {hasNews && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Recent News</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {newsArticles.map((article, i) => (
                  <a
                    key={i}
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'block', padding: '6px 8px', borderRadius: 6,
                      background: '#f9fafb', textDecoration: 'none',
                      borderLeft: '2px solid #e0e7ff',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div style={{
                      fontSize: 11, fontWeight: 500, color: '#1f2937', lineHeight: 1.3,
                      overflow: 'hidden', textOverflow: 'ellipsis',
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    }}>
                      {article.title}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                      <span style={{ fontSize: 10, color: '#6366f1', fontWeight: 600 }}>{article.source}</span>
                      <span style={{ fontSize: 10, color: '#9ca3af' }}>{formatTimeAgo(article.publishedAt)}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Follow-up */}
          <div style={{ paddingTop: 6, borderTop: '1px solid #f3f4f6' }}>
            {loggedInUser ? (
              <InlineLabelPills
                symbol={s.symbol}
                userEmail={loggedInUser.email}
                onOpenLabelManager={onOpenLabelManager}
              />
            ) : (
              <button
                onClick={(e) => { e.stopPropagation(); triggerLogin(); }}
                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#4f46e5', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                <BookmarkBorderIcon sx={{ fontSize: 13 }} /> Sign in to track
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(EarningsTile);
